import React, { useState, useEffect, useMemo } from 'react';
import { getMarginality } from '../api';

/**
 * Сторінка /marginality — чи дає IBKR кредит під кожен папір.
 *
 * 🚨 СТАНІВ ТРИ, А НЕ ДВА. Крім «кредит є» і «лише за свої» брокер має третій
 * вердикт: відмову у ВІДКРИТТІ позиції («No Opening Trades: Small Cap» або
 * «closing-only status»). Такий папір не можна купити навіть за власні гроші,
 * а рушій у бектесті його купує. Заради цієї колонки таблиця й будується:
 * сортуємо по тих, кому відмовлено, і шукаємо, за якою ознакою — капіталізація,
 * обіг чи ціна.
 */

/** Вердикт брокера з РЕАЛЬНОЇ маржі, а не з порогу бота.
 *
 * 🚨 Перша версія фарбувала все, що вище 60%, як «лише за свої» — бо 60% це
 * поріг, за яким бот вирішує, купувати чи ні. Але це політика бота, а не опис
 * дійсності: папір з маржею 62.5% брокер фінансує на 37.5%. Так було
 * неправильно позначено 382 папери. Кредит зникає лише на ~100%.
 */
function verdict(r) {
  const m = r.init_margin_pct;
  if (m === null || m === undefined) {
    return r.state === 'marginable' ? 'full' : (r.state === 'cash_only' ? 'none' : 'blocked');
  }
  if (r.state.startsWith('no_open') || r.state === 'rejected_other') return 'blocked';
  if (m >= 99.5) return 'none';
  if (m > 60)    return 'partial';
  return 'full';
}

const STATE_LABEL = {
  full:                { text: 'Кредит є',        cls: 'ok' },
  partial:             { text: 'Кредит частковий', cls: 'warn' },
  none:                { text: 'Лише за свої',    cls: 'bad' },
  blocked:             { text: 'Купити не можна', cls: 'bad' },
  marginable:          { text: 'Кредит є',        cls: 'ok' },
  cash_only:           { text: 'Лише за свої',    cls: 'bad' },
  no_open_smallcap:    { text: 'Не купити (small cap)', cls: 'bad' },
  no_open_closingonly: { text: 'Не купити (closing-only)', cls: 'bad' },
  rejected_other:      { text: 'Відмова брокера', cls: 'bad' },
  unknown:             { text: 'Не опитано',      cls: 'muted' },
};

const COLUMNS = [
  { key: 'name',            label: 'Компанія',        align: 'left'  },
  { key: 'ticker',          label: 'Тікер',           align: 'left'  },
  { key: 'market_cap',      label: 'Капіталізація',   align: 'right' },
  { key: 'adv_usd',         label: 'Обіг $/день (20 дн)', align: 'right' },
  { key: 'price',           label: 'Ціна',            align: 'right' },
  { key: 'state',           label: 'Кредит від IBKR', align: 'left'  },
  { key: 'init_margin_pct', label: 'Початкова маржа', align: 'right' },
];

function fmtMoney(v) {
  if (v === null || v === undefined) return '—';
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)} млрд`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)} млн`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)} тис`;
  return `$${v.toFixed(0)}`;
}

function fmtPrice(v) {
  if (v === null || v === undefined) return '—';
  return v < 1 ? `$${v.toFixed(4)}` : `$${v.toFixed(2)}`;
}

/** Зведення: як вердикт брокера залежить від ціни, капіталізації й обігу.
 *
 * Рахується тут-таки з тих самих рядків — окремого запиту не треба. Саме ця
 * розкладка й відповідає на питання, заради якого таблиця будувалась: чи є
 * закономірність у тому, кому брокер відмовляє.
 */
const BANDS = [
  {
    key: 'price', title: 'За ціною акції',
    bands: [[0, 1], [1, 2], [2, 3], [3, 5], [5, 10], [10, 20], [20, 50], [50, Infinity]],
    fmt: (a, b) => (b === Infinity ? `$${a}+` : `$${a}–${b}`),
  },
  {
    key: 'market_cap', title: 'За капіталізацією',
    bands: [[0, 5e7], [5e7, 3e8], [3e8, 1e9], [1e9, 1e10], [1e10, Infinity]],
    fmt: (a, b) => {
      const n = (v) => (v >= 1e9 ? `${v / 1e9} млрд` : `${v / 1e6} млн`);
      return b === Infinity ? `від ${n(a)}` : `${n(a)} – ${n(b)}`;
    },
  },
  {
    key: 'adv_usd', title: 'За обігом на день',
    bands: [[0, 1e5], [1e5, 1e6], [1e6, 1e7], [1e7, 1e8], [1e8, Infinity]],
    fmt: (a, b) => {
      const n = (v) => (v >= 1e6 ? `${v / 1e6} млн` : `${v / 1e3} тис`);
      return b === Infinity ? `від ${n(a)}` : `${n(a)} – ${n(b)}`;
    },
  },
];

function Summary({ rows }) {
  const isCredit  = (r) => ['full', 'partial'].includes(verdict(r));
  const isCash    = (r) => verdict(r) === 'none';
  const isBlocked = (r) => verdict(r) === 'blocked';

  return (
    <div style={{ display: 'grid', gap: 26 }}>
      {BANDS.map(({ key, title, bands, fmt }) => {
        const pool = rows.filter((r) => typeof r[key] === 'number');
        return (
          <div key={key}>
            <h3 style={S.h3}>{title}</h3>
            <div style={S.meta}>
              значення відоме для {pool.length} з {rows.length} паперів
            </div>
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={{ ...S.th, cursor: 'default' }}>Смуга</th>
                    <th style={{ ...S.th, cursor: 'default', textAlign: 'right' }}>Усього</th>
                    <th style={{ ...S.th, cursor: 'default', textAlign: 'right' }}>Кредит є</th>
                    <th style={{ ...S.th, cursor: 'default', textAlign: 'right' }}>Лише за свої</th>
                    <th style={{ ...S.th, cursor: 'default', textAlign: 'right' }}>Не купити</th>
                    <th style={{ ...S.th, cursor: 'default', textAlign: 'right' }}>% з кредитом</th>
                    <th style={{ ...S.th, cursor: 'default' }} />
                  </tr>
                </thead>
                <tbody>
                  {bands.map(([lo, hi]) => {
                    const g = pool.filter((r) => r[key] >= lo && r[key] < hi);
                    if (!g.length) return null;
                    const m = g.filter(isCredit).length;
                    const pct = Math.round((m / g.length) * 100);
                    return (
                      <tr key={`${lo}`} style={S.tr}>
                        <td style={S.td}>{fmt(lo, hi)}</td>
                        <td style={{ ...S.td, textAlign: 'right' }}>{g.length}</td>
                        <td style={{ ...S.td, textAlign: 'right' }}>{m}</td>
                        <td style={{ ...S.td, textAlign: 'right' }}>{g.filter(isCash).length}</td>
                        <td style={{ ...S.td, textAlign: 'right' }}>{g.filter(isBlocked).length}</td>
                        <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>{pct}%</td>
                        <td style={{ ...S.td, width: 160 }}>
                          <div style={{ background: '#F1F3F5', borderRadius: 3, height: 8 }}>
                            <div style={{ width: `${pct}%`, height: '100%', borderRadius: 3,
                                          background: '#00A656' }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Marginality() {
  const [data,    setData]    = useState({ rows: [], counts: {}, built_at: null });
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [sort,    setSort]    = useState({ key: 'market_cap', dir: 'desc' });
  const [filter,  setFilter]  = useState('all');
  const [search,  setSearch]  = useState('');
  const [view,    setView]    = useState('list');

  useEffect(() => {
    let alive = true;
    getMarginality()
      .then((res) => { if (alive) { setData(res.data); setError(res.data.error || ''); } })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const rows = useMemo(() => {
    let out = data.rows || [];
    if (filter === 'no_credit') {
      out = out.filter((r) => ['none', 'blocked'].includes(verdict(r)));
    } else if (filter === 'no_open') {
      out = out.filter((r) => verdict(r) === 'blocked');
    } else if (filter === 'marginable') {
      out = out.filter((r) => verdict(r) === 'full');
    } else if (filter === 'bot_rejects') {
      // Те, що бот НЕ купить при увімкненій галочці, хоча кредит під це є.
      out = out.filter((r) => verdict(r) === 'partial');
    }
    const q = search.trim().toUpperCase();
    if (q) out = out.filter((r) => r.ticker.includes(q) || (r.name || '').toUpperCase().includes(q));

    const { key, dir } = sort;
    const mul = dir === 'asc' ? 1 : -1;
    return [...out].sort((a, b) => {
      const x = a[key], y = b[key];
      // Порожні значення завжди в кінці, незалежно від напрямку сортування —
      // інакше «немає капіталізації» виглядало б як «найменша капіталізація».
      if (x === null || x === undefined || x === '') return 1;
      if (y === null || y === undefined || y === '') return -1;
      if (typeof x === 'number' && typeof y === 'number') return (x - y) * mul;
      return String(x).localeCompare(String(y)) * mul;
    });
  }, [data.rows, sort, filter, search]);

  const onSort = (key) => setSort((s) => (
    s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }
  ));

  const c = data.counts || {};
  const sum = (...keys) => keys.reduce((acc, k) => acc + (c[k] || 0), 0);

  return (
    <div style={S.wrap}>
      <h2 style={S.h2}>Маржинальність: під що IBKR дає кредит</h2>

      <p style={S.lead}>
        Для кожного паперу в брокера запитано, скільки він вимагатиме власних коштів
        на купівлю. Якщо 25% — решта кредит; якщо 100% — лише за свої. Окремо є папери,
        які брокер <b>не дозволяє купувати взагалі</b>: наявні позиції закрити можна,
        нові відкрити ні.
      </p>

      {data.built_at && (
        <div style={S.meta}>
          Дані зібрано {data.built_at} · паперів {data.total}
          {' · '}кредит є: <b>{c.marginable || 0}</b>
          {' · '}лише за свої: <b>{c.cash_only || 0}</b>
          {' · '}не купити: <b>{sum('no_open_smallcap', 'no_open_closingonly', 'rejected_other')}</b>
          {c.unknown ? ` · не опитано: ${c.unknown}` : ''}
        </div>
      )}

      <div style={{ ...S.controls, marginBottom: 18 }}>
        {[['list', 'Список акцій'], ['summary', 'Зведення по смугах']].map(([id, label]) => (
          <button key={id} onClick={() => setView(id)}
                  style={{ ...S.viewTab, ...(view === id ? S.viewTabActive : {}) }}>
            {label}
          </button>
        ))}
      </div>

      {view === 'summary' && !loading && !error && <Summary rows={data.rows || []} />}

      {view === 'list' && (<>
      <div style={S.controls}>
        {[['all', 'Усі'], ['marginable', 'Повний кредит'],
          ['bot_rejects', 'Кредит є, але робот пропустить'],
          ['no_credit', 'Без кредиту'], ['no_open', 'Заборонені до купівлі']].map(([id, label]) => (
          <button key={id} onClick={() => setFilter(id)}
                  style={{ ...S.tab, ...(filter === id ? S.tabActive : {}) }}>
            {label}
          </button>
        ))}
        <input value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="тікер або назва" style={S.search} />
        <span style={S.count}>показано {rows.length}</span>
      </div>

      {loading && <div style={S.info}>Завантаження…</div>}
      {error && <div style={S.err}>{error}</div>}

      {!loading && !error && (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                {COLUMNS.map((col) => (
                  <th key={col.key} onClick={() => onSort(col.key)}
                      style={{ ...S.th, textAlign: col.align }}>
                    {col.label}
                    {sort.key === col.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const st = STATE_LABEL[verdict(r)] || STATE_LABEL.unknown;
                return (
                  <tr key={r.ticker} style={S.tr} title={r.reason || ''}>
                    <td style={S.td}>{r.name || '—'}</td>
                    <td style={{ ...S.td, fontWeight: 600 }}>{r.ticker}</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{fmtMoney(r.market_cap)}</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{fmtMoney(r.adv_usd)}</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{fmtPrice(r.price)}</td>
                    <td style={S.td}><span style={{ ...S.badge, ...S.badgeCls[st.cls] }}>{st.text}</span></td>
                    <td style={{ ...S.td, textAlign: 'right' }}>
                      {r.init_margin_pct === null ? '—' : `${r.init_margin_pct.toFixed(1)}%`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </>)}
    </div>
  );
}

const S = {
  // Палітра кабінету (та сама, що в TradingAccountPage): світлий фон, темний
  // текст. Перша версія була в темних кольорах і на білому тлі не читалась.
  wrap:  { maxWidth: 1400, margin: '0 auto', padding: '24px 16px', color: '#1A1A1A' },
  h2:    { margin: '0 0 8px', fontSize: 22, color: '#1A1A1A' },
  lead:  { margin: '0 0 12px', color: '#5B646F', lineHeight: 1.5, maxWidth: 900 },
  meta:  { margin: '0 0 16px', fontSize: 13, color: '#5B646F' },
  controls: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 },
  tab:   { padding: '6px 12px', borderRadius: 6, border: '1px solid #E5E7EB',
           background: '#FFFFFF', color: '#5B646F', cursor: 'pointer', fontSize: 13 },
  tabActive: { background: '#475FFA', borderColor: '#475FFA', color: '#fff' },
  search: { padding: '6px 10px', borderRadius: 6, border: '1px solid #E5E7EB',
            background: '#FFFFFF', color: '#1A1A1A', fontSize: 13, minWidth: 180 },
  count: { fontSize: 12, color: '#5B646F', marginLeft: 'auto' },
  tableWrap: { overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: 8,
               background: '#FFFFFF' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th:    { padding: '10px 12px', background: '#F9FAFB', color: '#5B646F',
           cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
           borderBottom: '1px solid #E5E7EB', position: 'sticky', top: 0,
           fontWeight: 600 },
  tr:    { borderBottom: '1px solid #F1F3F5' },
  td:    { padding: '8px 12px', color: '#1A1A1A', whiteSpace: 'nowrap',
           fontVariantNumeric: 'tabular-nums' },
  badge: { padding: '2px 8px', borderRadius: 4, fontSize: 12, whiteSpace: 'nowrap',
           fontWeight: 500 },
  badgeCls: {
    ok:    { background: '#E7F7EF', color: '#007A41' },
    warn:  { background: '#FEF3C7', color: '#92400E' },
    bad:   { background: '#FDE8EF', color: '#C02B5E' },
    muted: { background: '#F1F3F5', color: '#5B646F' },
  },
  h3:    { margin: '0 0 4px', fontSize: 16, color: '#1A1A1A' },
  viewTab: { padding: '8px 16px', borderRadius: 8, border: '1px solid #E5E7EB',
             background: '#FFFFFF', color: '#5B646F', cursor: 'pointer',
             fontSize: 14, fontWeight: 500 },
  viewTabActive: { background: '#101220', borderColor: '#101220', color: '#fff' },
  info:  { padding: 16, color: '#5B646F' },
  err:   { padding: 12, background: '#FDE8EF', border: '1px solid #F5C2D3',
           borderRadius: 6, color: '#C02B5E', marginBottom: 12 },
};
